import { css, useTheme } from '@emotion/react'
import styled from '@emotion/styled'
import { useLayoutEffect } from 'react'
import { flushSync } from 'react-dom'
import { observer } from 'mobx-react'

import { HelpIcon, SVGWrapper, TooltipText } from '../../../../components'
import { Search } from '../../../../assets/icons'
import { releaseKeyboardPrimer } from '../../../../utils'
import { IconButton, RemoveIcon, SectionBox } from '../../styled'
import {
  EmptyMessage,
  FocusedFooter,
  FocusedHeading,
  FocusedScroll,
  HeadingRow,
  ListHeading,
} from '../AddNewInvestmentStyle'
import { InputRow } from './SecuritySearch'
import { SecurityRowList } from './SecurityRows'
import useSecuritySearch from './useSecuritySearch'

/* Design: 20px in from every edge, with the underline sitting 29px clear of the bottom. */
const SearchBox = styled(SectionBox)`
  padding: 20px 20px 29px;
`

/**
 * The security search as a screen of its own, for phones.
 *
 * Where desktop hangs the results off the field in a pop-up, here they are drawn straight onto the
 * screen underneath it — a pop-up would land behind the keyboard. The screen is the shell's
 * scrolling region plus its fixed footer, so it renders both as siblings for the shell to lay out.
 *
 * @param {Object} props
 * @param {string} props.accountType account type key from the route, e.g. '401k'
 * @param {Array} props.notInvestedInvestments investments already marked not invested
 * @param {Function} props.onSelect called with the chosen security's id
 * @param {Function} props.onScroll tells the shell's header when content has run under it
 * @param {Object} [props.inputRef] ref for the search field, so the flow can focus it on re-entry
 */
const SecuritySearchScreen = props => {
  const { accountType, notInvestedInvestments, onSelect, onScroll, inputRef } = props

  const theme = useTheme()
  const search = useSecuritySearch({ accountType, notInvestedInvestments, inputRef })
  const {
    inputRef: searchInputRef,
    query,
    results,
    searching,
    browsingGenerics,
    genericAssetClasses,
    notInvestedOptions,
    content,
    focusInput,
    handleChange,
    resetSearch,
    browseGenerics,
  } = search

  /*
   * Takes the cursor on arrival, and with it the keyboard that the tap which led here raised on a
   * stand-in field. Done in an effect rather than with autoFocus so the order is ours: the real
   * field takes the focus, and only then is the stand-in dropped. Dropping it first would close the
   * keyboard before this field could keep it open. See primeKeyboard.
   */
  useLayoutEffect(() => {
    focusInput()
    releaseKeyboardPrimer()
    // Only on arrival; the other ways back to the field focus it themselves.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /*
   * Coming back from the generic asset classes remounts the search field, which mobile browsers
   * only raise the keyboard for when focus() runs inside the tap that asked for it. flushSync puts
   * the field on screen while the gesture is still live so the focus below counts.
   */
  const handleBackToSearch = () => {
    flushSync(() => search.backToSearch())
    focusInput()
  }

  return (
    <>
      <FocusedScroll onScroll={onScroll}>
        <HeadingRow>
          <FocusedHeading>Find investment</FocusedHeading>
          <HelpIcon tooltip={TooltipText.findInvestment()} />
        </HeadingRow>

        {/* Browsing the generic asset classes replaces the field rather than sitting under it. */}
        {!browsingGenerics && (
          <SearchBox>
            <InputRow>
              <SVGWrapper
                svg={Search}
                size='large'
                margin='0px'
                fill={theme.lightestGray}
                cursor='text'
                onClick={focusInput}
              />
              <input
                ref={searchInputRef}
                name='securitySearch'
                type='text'
                autoComplete='off'
                aria-label='Find investment'
                placeholder='Search by name or ticker'
                value={query}
                onChange={handleChange}
              />
              {query.length > 0 && (
                <IconButton
                  type='button'
                  aria-label='Clear search'
                  onClick={resetSearch}
                  css={css`
                    flex: 0 0 auto;
                  `}>
                  <RemoveIcon />
                </IconButton>
              )}
            </InputRow>
          </SearchBox>
        )}

        <div role='listbox'>
          {/* Nothing typed and nothing set aside as "not invested" leaves this area blank. */}
          {content === 'notInvested' && (
            <>
              <ListHeading bold>Your plan&rsquo;s investment options</ListHeading>
              <SecurityRowList securities={notInvestedOptions} onSelect={onSelect} mobile />
            </>
          )}

          {content === 'results' && (
            <>
              {results.length > 0 && (
                <SecurityRowList securities={results} onSelect={onSelect} mobile />
              )}
              {results.length === 0 && (
                <EmptyMessage>
                  {searching ? 'Searching…' : 'No investments match your search'}
                </EmptyMessage>
              )}
            </>
          )}

          {content === 'generics' && (
            <>
              <ListHeading>Choose from general asset classes</ListHeading>
              {genericAssetClasses.map(({ label, securities }) => (
                <div key={label}>
                  <ListHeading bold>{label}</ListHeading>
                  <SecurityRowList securities={securities} onSelect={onSelect} mobile />
                </div>
              ))}
            </>
          )}
        </div>
      </FocusedScroll>

      {/* The footer belongs to the screen, not to a set of results: an account whose investments
          cannot be marked "not invested" opens on a blank list, and the generic asset classes have
          to be reachable from there without typing something first to make the link appear. */}
      {browsingGenerics ? (
        <FocusedFooter type='button' onClick={handleBackToSearch}>
          <SVGWrapper
            svg={Search}
            size='large'
            margin='0px'
            fill={theme.lightestGray}
            cursor='pointer'
          />
          Search by name or ticker
        </FocusedFooter>
      ) : (
        <FocusedFooter type='button' onClick={browseGenerics}>
          Can&rsquo;t find your investment?
        </FocusedFooter>
      )}
    </>
  )
}

export default observer(SecuritySearchScreen)
